import { ProductDetailQuery } from "@/graphql";
import { CreditPriceOption } from "@/types/products";
import { parseDecimal } from "@/utils/string";

export enum PriceUpdateConfirmationStep {
  INITIAL,
  SUCCESS,
  ERROR,
}

export const DEFAULT_TTL_FOR_LEGACY_PRICING_CLIENTS = 1000 * 60 * 60; // 1hr

export function getConflictingLegacyPricingClients(
  coachId: string,
  productId: string,
): string[] {
  const key = `legacyPricingClientsByCoach_${coachId}_v2`;

  try {
    const item = window.localStorage.getItem(key);
    if (!item) return [];

    const parsedItem = JSON.parse(item);

    // Check if the item has expired
    if (parsedItem.expiryTime && new Date().getTime() > parsedItem.expiryTime) {
      window.localStorage.removeItem(key);
      return [];
    }

    const lastLegacyPricing = parsedItem.value || {
      productId: undefined,
      clientIds: [],
    };

    // Only return clients when viewing a different coach product
    // because legacy pricing clients for the same coach product
    // has already been excluded from the confirmation modal
    if (!lastLegacyPricing.productId) return [];
    if (lastLegacyPricing.productId === productId) return [];

    return lastLegacyPricing.clientIds;
  } catch (error) {
    console.error(
      `Error retrieving legacy pricing for coach ${coachId}:`,
      error,
    );
    return [];
  }
}

export function generateAllPriceOptions(
  coachProduct: ProductDetailQuery["productById"]["coachProduct"],
  currentPrice?: string | null,
): CreditPriceOption[] {
  let validPrices = coachProduct.validPrices || [];
  if (currentPrice && !validPrices.includes(currentPrice)) {
    validPrices = [...validPrices, currentPrice];
  }

  let invalidPrices = coachProduct.invalidPrices || [];
  invalidPrices = invalidPrices.filter((price) => price !== currentPrice);

  const priceOptions: CreditPriceOption[] = [
    ...validPrices.map((price) => ({
      value: price,
      label: price,
      isLocked: false,
    })),
    ...invalidPrices.map((price) => ({
      value: price,
      label: price,
      isLocked: true,
    })),
  ];

  return priceOptions.sort((a, b) => {
    return parseDecimal(a.value) - parseDecimal(b.value);
  });
}
